import React from "react";
import { motion } from "framer-motion";
import { CalendarCheck, MessageSquare, Map, Clock, Users } from "lucide-react";

const FloatingParticle = ({ delay, x, y, size = 4 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${x}%`,
      top: `${y}%`,
      background: "linear-gradient(135deg, #60a5fa, #818cf8)",
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0],
      y: [0, -30, -60],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

const FellowsProgramFormatSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <FloatingParticle delay={0} x={15} y={25} size={5} />
      <FloatingParticle delay={1.2} x={80} y={55} size={4} />
      <FloatingParticle delay={0.6} x={45} y={75} size={6} />
      <FloatingParticle delay={1.8} x={70} y={20} size={3} />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1100px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4 border border-blue-100/60 shadow-sm">
            <CalendarCheck className="w-4 h-4 text-blue-500" />
            <span>Program Format</span>
          </div>
          <h2 className="text-3xl lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Influence the{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Product Roadmap.
            </span>
          </h2>
          <p className="text-[16px] text-slate-500 font-medium mt-4 max-w-2xl mx-auto">
            No extra time commitment — most customers already do a monthly check-in with their AM. We just add structured surveys during yours.
          </p>
        </motion.div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="w-full rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative mb-8"
        >
          <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #0ea5e9 100%)" }} />

          <div className="p-10 flex gap-10 items-center">
            {/* Left: visual */}
            <div className="shrink-0 flex flex-col items-center gap-3">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
                className="w-28 h-28 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 flex flex-col items-center justify-center shadow-xl shadow-blue-500/20 border border-blue-400"
              >
                <span className="text-[36px] font-extrabold text-white leading-none">30</span>
                <span className="text-[12px] font-bold text-blue-200 uppercase tracking-wider">min / mo</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100"
              >
                <Clock className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-[11px] font-bold text-blue-600">Monthly check-in</span>
              </motion.div>
            </div>

            {/* Right: details */}
            <div className="flex-1">
              <h3 className="text-[22px] font-bold text-slate-900 mb-2">Share Your Ideas During Your Regular AM Meetings</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed font-medium mb-6">
                Most Asha customers already have a monthly 30-minute call with their account manager. As a Fellow, nothing changes except we'll include structured surveys and feedback sessions during that time — giving you a direct voice into what we build next.
              </p>

              <div className="grid grid-cols-3 gap-4">
                {[
                  {
                    icon: Users,
                    title: "Practice Optimization",
                    desc: "Get hands-on help maximizing how your practice uses Asha — workflows, agent tuning, growth strategies.",
                    color: "blue",
                  },
                  {
                    icon: MessageSquare,
                    title: "Structured Surveys",
                    desc: "We'll bring structured feedback surveys to your check-in — your responses go directly to our product team and shape what gets built.",
                    color: "indigo",
                  },
                  {
                    icon: Map,
                    title: "Product Roadmap Preview",
                    desc: "Your AM will walk you through what's coming next so you can weigh in before features ship.",
                    color: "sky",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                    className={`rounded-[16px] bg-${item.color}-50/80 border border-${item.color}-100/60 p-5 flex flex-col`}
                  >
                    <div className={`w-9 h-9 rounded-xl bg-white flex items-center justify-center mb-3 shadow-sm border border-${item.color}-100`}>
                      <item.icon className={`w-4.5 h-4.5 text-${item.color}-600`} />
                    </div>
                    <h4 className="text-[14px] font-bold text-slate-900 mb-1.5">{item.title}</h4>
                    <p className="text-[12px] text-slate-500 leading-relaxed font-medium">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default FellowsProgramFormatSlide;
